// components/ui/StepRail.tsx
// Replaces the old six-tile ProbateWizard stepper.
// A thin progress rail + roman-numeral indicator of the active step.

import React from 'react';

interface StepRailProps {
    steps: Array<{ key: string; label: string }>;
    currentIndex: number;               // 0-based
    completedIndices?: number[];
    onStepClick?: (index: number) => void;
}

const ROMAN = [
    'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
    'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX',
];

export function StepRail({ steps, currentIndex, completedIndices = [], onStepClick }: StepRailProps) {
    return (
        <div className="mb-8">
            {/* Progress ticks */}
            <div className="flex gap-1 mb-4">
                {steps.map((_, i) => (
                    <div
                        key={i}
                        className={`flex-1 h-0.5 ${
                            i <= currentIndex ? 'bg-[#3730A3]' : 'bg-[#E8E4DB]'
                        }`}
                    />
                ))}
            </div>
            {/* Active step marker */}
            <div className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-3">
                    <span className="font-serif italic text-[15px] text-[#3730A3]">
                        Step {ROMAN[currentIndex]}
                    </span>
                    <span className="text-[12px] text-[#6B6762]">
                        of {ROMAN[steps.length - 1]}
                    </span>
                </div>
                <div className="font-mono text-[10px] tracking-[0.14em] text-[#6B6762]">
                    {String(currentIndex + 1).padStart(2, '0')} / {String(steps.length).padStart(2, '0')}
                </div>
            </div>
            {/* Jump nav (optional) */}
            {onStepClick && (
                <nav className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
                    {steps.map((step, i) => {
                        const isActive = i === currentIndex;
                        const isDone = completedIndices.includes(i);
                        return (
                            <button
                                key={step.key}
                                type="button"
                                onClick={() => onStepClick(i)}
                                className={`
                                    text-[11px] transition-colors
                                    ${isActive ? 'text-[#3730A3] font-semibold' :
                                      isDone ? 'text-[#1A1A1A] hover:text-[#3730A3]' :
                                               'text-[#AFA99F]'}
                                `}
                            >
                                {ROMAN[i]}. {step.label}
                            </button>
                        );
                    })}
                </nav>
            )}
        </div>
    );
}
